import { useEffect, useState } from "react";
import { api, clearToken } from "@/lib/api";
import Watermark from "@/components/app/Watermark";
import AntiCopyGuard from "@/components/app/AntiCopyGuard";
import { formatIST } from "@/lib/time";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { LogOut, Search, MessageCircleWarning, ChevronRight, ArrowLeft, X, BookOpen, ClipboardEdit, LineChart, Coins, Edit3, Check, FileText } from "lucide-react";
import DailyEntry from "@/components/ops/DailyEntry";
import PnLDiagnostic from "@/components/ops/PnLDiagnostic";
import ItemProfit from "@/components/ops/ItemProfit";
import PrepMethodTab from "@/components/ops/PrepMethodTab";

export default function Viewer({ user, onLogout }) {
  const perms = user.permissions || {};
  const canViewMakingCost = perms.view_making_cost !== false;
  const canViewRecipe = perms.view_recipe !== false;
  const canViewMethod = perms.view_method !== false;
  const canSuggest = perms.view_suggestions !== false;
  const canEdit = perms.can_edit === true;
  const hasOutlet = !!user.outlet_id;
  const canEntry = hasOutlet && perms.cc_daily_entry !== false;   // default ON so daily reporting works after migration
  const canPnL   = hasOutlet && perms.cc_pnl === true;
  const canItem  = hasOutlet && perms.cc_item_profit === true;
  const canPrep  = perms.prep_view === true;
  const hasAnyCC = canEntry || canPnL || canItem || canPrep;
  const [mode, setMode] = useState("sop");
  const [cats, setCats] = useState([]);
  const [editingSP, setEditingSP] = useState(false);
  const [editingCost, setEditingCost] = useState(false);
  const [editVal, setEditVal] = useState("");
  const [selectedCat, setSelectedCat] = useState(null);
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [q, setQ] = useState("");
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [sField, setSField] = useState("ingredient");
  const [sTarget, setSTarget] = useState("");
  const [sMsg, setSMsg] = useState("");
  const [sImageUrl, setSImageUrl] = useState("");
  const [sUploading, setSUploading] = useState(false);
  const [globalQ, setGlobalQ] = useState("");
  const [scope, setScope] = useState("all");
  const [searchRes, setSearchRes] = useState(null);

  useEffect(() => {
    if (!globalQ.trim()) { setSearchRes(null); return; }
    const t = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(globalQ)}&scope=${scope}`).then(r => setSearchRes(r.data));
    }, 250);
    return () => clearTimeout(t);
  }, [globalQ, scope]);

  const openItemById = async (id) => {
    const { data } = await api.get(`/items/${id}`);
    const cat = cats.find(c => c.id === data.category_id);
    if (cat) setSelectedCat(cat);
    setTimeout(() => setSelectedItem(data), 50);
    setGlobalQ("");
  };

  useEffect(() => { api.get("/categories").then(r => setCats(r.data)); }, []);
  useEffect(() => {
    if (!selectedCat) return;
    api.get(`/items?category_id=${selectedCat.id}`).then(r => setItems(r.data));
    setSelectedItem(null);
  }, [selectedCat]);

  const openSuggest = (field, target) => {
    setSField(field); setSTarget(target); setSMsg(""); setSImageUrl(""); setSuggestOpen(true);
  };
  const saveField = async (field) => {
    const raw = parseFloat(editVal);
    if (isNaN(raw) || raw < 0) return toast.error("Enter a number ≥ 0");
    try {
      await api.patch(`/items/${selectedItem.id}`, { [field]: raw });
      const { data } = await api.get(`/items/${selectedItem.id}`);
      setSelectedItem(data);
      setEditingSP(false); setEditingCost(false); setEditVal("");
      toast.success(field === "sp" ? "Selling price updated" : "Making cost updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };
  const submitSuggest = async () => {
    try {
      await api.post("/suggestions", {
        item_id: selectedItem.id, ingredient_name: sField === "ingredient" ? sTarget : "",
        field: sField, message: sMsg.trim(), image_url: sImageUrl || null
      });
      toast.success("Suggestion sent to admin");
      setSuggestOpen(false);
    } catch { toast.error("Failed to send"); }
  };
  const uploadSuggestImage = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setSUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const t = localStorage.getItem("cc_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads`, {
        method: "POST", headers: { Authorization: `Bearer ${t}` }, body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Upload failed");
      setSImageUrl(data.url); toast.success("Photo attached");
    } catch (err) { toast.error(err.message || "Upload failed"); }
    finally { setSUploading(false); e.target.value = ""; }
  };

  const filtered = q ? items.filter(i => i.name.toLowerCase().includes(q.toLowerCase())) : items;

  return (
    <AntiCopyGuard>
      <div className="theme-viewer min-h-screen" style={{ background: "var(--viewer-bg)", color: "var(--viewer-text)" }}>
        <Watermark email={user.email} />

        <header className="sticky top-0 z-30 border-b" style={{ background: "rgba(253,251,247,0.92)", backdropFilter: "blur(10px)", borderColor: "#e2dccb" }}>
          <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-4">
            <div>
              <div className="font-mono2 text-[10px] tracking-[0.35em] uppercase" style={{ color: "#8a5a1a" }}>Confidential Franchise Document</div>
              <div className="font-display text-xl leading-none mt-1">Chai Chaska Cafe · SOP</div>
            </div>
            <div className="flex items-center gap-3">
              {hasAnyCC && (
                <div className="hidden md:flex items-center rounded-full border p-1" style={{ borderColor: "#e2dccb", background: "#fffaf0" }}>
                  <button data-testid="viewer-mode-sop" onClick={() => setMode("sop")}
                    className={`px-3 h-7 rounded-full text-[10px] font-mono2 uppercase tracking-widest flex items-center gap-1 ${mode === "sop" ? "bg-[#3B2519] text-[#F5C242]" : "text-[#6b5a45] hover:text-black"}`}>
                    <BookOpen size={12} /> SOP
                  </button>
                  {canEntry && (
                    <button data-testid="viewer-mode-entry" onClick={() => setMode("entry")}
                      className={`px-3 h-7 rounded-full text-[10px] font-mono2 uppercase tracking-widest flex items-center gap-1 ${mode === "entry" ? "bg-[#3B2519] text-[#F5C242]" : "text-[#6b5a45] hover:text-black"}`}>
                      <ClipboardEdit size={12} /> Daily entry
                    </button>
                  )}
                  {canPnL && (
                    <button data-testid="viewer-mode-pnl" onClick={() => setMode("pnl")}
                      className={`px-3 h-7 rounded-full text-[10px] font-mono2 uppercase tracking-widest flex items-center gap-1 ${mode === "pnl" ? "bg-[#3B2519] text-[#F5C242]" : "text-[#6b5a45] hover:text-black"}`}>
                      <LineChart size={12} /> P&amp;L
                    </button>
                  )}
                  {canItem && (
                    <button data-testid="viewer-mode-item" onClick={() => setMode("item")}
                      className={`px-3 h-7 rounded-full text-[10px] font-mono2 uppercase tracking-widest flex items-center gap-1 ${mode === "item" ? "bg-[#3B2519] text-[#F5C242]" : "text-[#6b5a45] hover:text-black"}`}>
                      <Coins size={12} /> Item profit
                    </button>
                  )}
                  {canPrep && (
                    <button data-testid="viewer-mode-prep" onClick={() => setMode("prep")}
                      className={`px-3 h-7 rounded-full text-[10px] font-mono2 uppercase tracking-widest flex items-center gap-1 ${mode === "prep" ? "bg-[#3B2519] text-[#F5C242]" : "text-[#6b5a45] hover:text-black"}`}>
                      <FileText size={12} /> Methods
                    </button>
                  )}
                </div>
              )}
              <div className="relative w-52 md:w-80">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-60" />
                <Input data-testid="global-search" value={globalQ} onChange={e => setGlobalQ(e.target.value)}
                  placeholder="Search category, item, ingredient" className="pl-9 pr-8 h-10 bg-white" />
                {globalQ && <button onClick={() => setGlobalQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 opacity-60"><X size={14} /></button>}
              </div>
              <select data-testid="scope-select" value={scope} onChange={e => setScope(e.target.value)}
                className="hidden md:block h-10 px-2 rounded-md border bg-white text-xs font-mono2 uppercase tracking-widest" style={{ borderColor: "#e2dccb" }}>
                <option value="all">All</option><option value="category">Category</option>
                <option value="item">Item</option><option value="ingredient">Ingredient</option>
              </select>
              <span className="hidden md:block text-xs font-mono2 uppercase tracking-widest" style={{ color: "#6b5a45" }}>{user.email}</span>
              <a href="/profile" data-testid="viewer-profile-link" className="hidden md:flex items-center gap-2 text-xs font-mono2 uppercase tracking-widest hover:text-black" style={{ color: "#6b5a45" }}>Profile</a>
              <button data-testid="logout-btn" onClick={onLogout} className="flex items-center gap-2 text-xs font-mono2 uppercase tracking-widest hover:text-black" style={{ color: "#6b5a45" }}>
                <LogOut size={14} /> Sign out
              </button>
            </div>
          </div>
        </header>

        {searchRes && (
          <div className="max-w-6xl mx-auto px-6 pt-6 relative z-20">
            <div className="rounded-2xl border bg-white p-6" style={{ borderColor: "#e2dccb" }}>
              <div className="font-mono2 text-[10px] tracking-[0.3em] uppercase mb-3" style={{ color: "#8a5a1a" }}>Search results for {`"${globalQ}"`}</div>
              {searchRes.categories.length > 0 && (<div className="mb-4"><div className="text-xs font-semibold mb-1">Categories</div>
                <div className="flex flex-wrap gap-2">{searchRes.categories.map(c => (
                  <button key={c.id} data-testid={`sr-cat-${c.id}`} onClick={() => { setSelectedCat(c); setGlobalQ(""); }}
                    className="px-3 py-1 rounded-full text-sm bg-[#f7efd9] hover:bg-[#F5C242]">{c.name}</button>))}</div></div>)}
              {searchRes.items.length > 0 && (<div className="mb-4"><div className="text-xs font-semibold mb-1">Items</div>
                <div className="grid sm:grid-cols-2 gap-2">{searchRes.items.map(it => (
                  <button key={it.id} data-testid={`sr-item-${it.id}`} onClick={() => openItemById(it.id)}
                    className="text-left p-3 rounded-lg border hover:border-[#8a5a1a]" style={{ borderColor: "#e2dccb" }}>
                    <div className="font-medium">{it.name}</div><div className="text-xs opacity-60">SP ₹{it.sp}</div></button>))}</div></div>)}
              {searchRes.ingredient_matches.length > 0 && (<div><div className="text-xs font-semibold mb-1">Ingredient matches</div>
                <div className="space-y-1">{searchRes.ingredient_matches.map((m, i) => (
                  <button key={i} data-testid={`sr-ing-${m.item.id}`} onClick={() => openItemById(m.item.id)}
                    className="w-full text-left p-3 rounded-lg border hover:border-[#8a5a1a]" style={{ borderColor: "#e2dccb" }}>
                    <div className="font-medium">{m.item.name}</div>
                    <div className="text-xs opacity-70">Matches: {m.matched_ingredients.map(x => x.name).join(", ")}</div></button>))}</div></div>)}
              {searchRes.categories.length === 0 && searchRes.items.length === 0 && searchRes.ingredient_matches.length === 0 && (
                <div className="opacity-60 text-sm">Nothing found</div>)}
            </div>
          </div>
        )}

        <main className="max-w-6xl mx-auto px-6 py-10 md:py-14 relative z-20">
          {mode === "prep" && canPrep ? (
            <div className="space-y-4">
              <PrepMethodTab canEdit={perms.prep_edit === true} email={user.email} />
              <div className="md:hidden flex justify-center pt-3">
                <button onClick={() => setMode("sop")} className="px-4 py-2 rounded-full text-xs font-mono2 uppercase tracking-widest bg-[#3B2519] text-[#F5C242]">← Back to SOP</button>
              </div>
            </div>
          ) : mode === "item" && canItem ? (
            <div className="space-y-4">
              <div className="rounded-2xl p-6 border" style={{ background: "#fffaf0", borderColor: "#e2dccb" }}>
                <div className="font-mono2 text-[10px] tracking-[0.3em] uppercase mb-1" style={{ color: "#8a5a1a" }}>Item-wise net profit</div>
                <h2 className="font-display text-2xl md:text-3xl" style={{ color: "#2b1a0d" }}>What each item is really making</h2>
                <p className="mt-1 text-sm" style={{ color: "#6b5a45" }}>Enter units sold per item this month. We combine that with recipe costs + your logged expenses to show revenue, gross profit and allocated overhead → net profit per item.</p>
              </div>
              <ItemProfit />
              <div className="md:hidden flex justify-center pt-3">
                <button onClick={() => setMode("sop")} className="px-4 py-2 rounded-full text-xs font-mono2 uppercase tracking-widest bg-[#3B2519] text-[#F5C242]">← Back to SOP</button>
              </div>
            </div>
          ) : mode === "pnl" && canPnL ? (
            <div className="space-y-4">
              <div className="rounded-2xl p-6 border" style={{ background: "#fffaf0", borderColor: "#e2dccb" }}>
                <div className="font-mono2 text-[10px] tracking-[0.3em] uppercase mb-1" style={{ color: "#8a5a1a" }}>Your outlet · profit story</div>
                <h2 className="font-display text-2xl md:text-3xl" style={{ color: "#2b1a0d" }}>P&amp;L diagnostic</h2>
                <p className="mt-1 text-sm" style={{ color: "#6b5a45" }}>Revenue → −Variable = Contribution → −Fixed = EBITDA. Read the waterfall left to right to see where the money goes.</p>
              </div>
              <PnLDiagnostic />
              <div className="md:hidden flex justify-center pt-3">
                <button data-testid="viewer-mode-sop-mobile" onClick={() => setMode("sop")}
                  className="px-4 py-2 rounded-full text-xs font-mono2 uppercase tracking-widest bg-[#3B2519] text-[#F5C242]">
                  ← Back to SOP
                </button>
              </div>
            </div>
          ) : mode === "entry" && canEntry ? (
            <div className="space-y-4">
              <div className="rounded-2xl p-6 border" style={{ background: "#fffaf0", borderColor: "#e2dccb" }}>
                <div className="font-mono2 text-[10px] tracking-[0.3em] uppercase mb-1" style={{ color: "#8a5a1a" }}>End-of-day · outlet manager</div>
                <h2 className="font-display text-2xl md:text-3xl" style={{ color: "#2b1a0d" }}>Daily sales &amp; live expenses</h2>
                <p className="mt-1 text-sm" style={{ color: "#6b5a45" }}>Log the day&apos;s sales by channel and every expense as it happens — head office sees this in real time.</p>
              </div>
              <DailyEntry />
              {/* Mobile-only mode switch (mirrors the header toggle which is hidden on small screens) */}
              <div className="md:hidden flex justify-center pt-3">
                <button data-testid="viewer-mode-sop-mobile" onClick={() => setMode("sop")}
                  className="px-4 py-2 rounded-full text-xs font-mono2 uppercase tracking-widest bg-[#3B2519] text-[#F5C242]">
                  ← Back to SOP
                </button>
              </div>
            </div>
          ) : (
          <div className="grid md:grid-cols-[260px_1fr] gap-8 md:gap-12">
          {/* Mobile-only entry shortcut for outlet managers */}
          {canPrep && (
            <div className="md:hidden -mt-4 mb-4">
              <button data-testid="viewer-mode-prep-mobile" onClick={() => setMode("prep")}
                className="w-full px-4 py-3 rounded-2xl text-sm font-medium flex items-center justify-center gap-2 border bg-white"
                style={{ color: "#3B2519", borderColor: "#e2dccb" }}>
                <FileText size={16} /> Preparation methods
              </button>
            </div>
          )}
          {hasOutlet && (
            <div className="md:hidden -mt-4 mb-4">
              <button data-testid="viewer-mode-entry-mobile" onClick={() => setMode("entry")}
                className="w-full px-4 py-3 rounded-2xl text-sm font-medium flex items-center justify-center gap-2 border"
                style={{ background: "#3B2519", color: "#F5C242", borderColor: "#e2dccb" }}>
                <ClipboardEdit size={16} /> Go to daily entry
              </button>
            </div>
          )}
          <aside className="space-y-1">
            <div className="font-mono2 text-[10px] tracking-[0.3em] uppercase mb-3" style={{ color: "#8a5a1a" }}>Categories</div>
            {cats.map(c => (
              <button key={c.id} data-testid={`cat-${c.id}`}
                onClick={() => setSelectedCat(c)}
                className={`w-full text-left px-4 py-3 rounded-xl border transition-colors ${selectedCat?.id === c.id ? "bg-[#3B2519] text-[#F5C242] border-transparent" : "border-transparent hover:bg-[#efe6d3]"}`}>
                <div className="font-display text-lg">{c.name}</div>
              </button>
            ))}
          </aside>

          <section>
            {!selectedCat && (
              <div className="rounded-2xl p-8 md:p-10 border" style={{ borderColor: "#e2dccb", background: "#fffaf0" }} data-testid="viewer-welcome-hero">
                <div className="font-mono2 text-[10px] tracking-[0.4em] uppercase mb-2" style={{ color: "#8a5a1a" }}>Signed in</div>
                <h1 className="font-display font-bold text-3xl sm:text-4xl md:text-5xl leading-[1.05]" style={{ color: "#2b1a0d" }}>
                  Welcome to <span style={{ color: "#B4841F" }}>Chai Chaska Cafe Vault</span>
                </h1>
                <p className="mt-3 max-w-lg text-sm md:text-base" style={{ color: "#4a3f2f" }}>
                  Select a category on the left to explore recipes, ingredients and pricing. Spot something off? Tap any ingredient to send a discrepancy note to head office.
                </p>
                <div className="mt-4 text-[10px] font-mono2 uppercase tracking-[0.3em]" style={{ color: "#8a5a1a" }}>
                  Confidential Franchise Document · Watermarked with your email &amp; IST timestamp
                </div>
              </div>
            )}

            {selectedCat && !selectedItem && (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <h2 className="font-display text-3xl md:text-4xl flex-1">{selectedCat.name}</h2>
                  <div className="relative w-full max-w-xs">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-60" />
                    <Input data-testid="item-search-input" value={q} onChange={e => setQ(e.target.value)}
                      placeholder="Search item" className="pl-9 bg-white" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  {filtered.map(it => (
                    <button key={it.id} data-testid={`item-card-${it.id}`}
                      onClick={() => setSelectedItem(it)}
                      className="text-left p-5 rounded-2xl border bg-white hover:border-[#8a5a1a] transition-colors">
                      <div className="font-display text-xl">{it.name}</div>
                      <div className="mt-2 flex items-center justify-between text-sm">
                        <span className="font-mono2 text-xs uppercase tracking-widest" style={{ color: "#8a5a1a" }}>
                          SP ₹{it.sp}
                        </span>
                        <span className="opacity-60">{it.ingredients?.length || 0} items <ChevronRight size={14} className="inline" /></span>
                      </div>
                    </button>
                  ))}
                  {filtered.length === 0 && <div className="opacity-60 text-sm">No items</div>}
                </div>
              </>
            )}

            {selectedItem && (
              <div>
                <button data-testid="back-to-list-btn" onClick={() => setSelectedItem(null)}
                  className="flex items-center gap-2 text-xs font-mono2 uppercase tracking-widest mb-4 opacity-70 hover:opacity-100">
                  <ArrowLeft size={14} /> Back to {selectedCat.name}
                </button>
                <div className="rounded-2xl border bg-white p-8 md:p-10 relative" style={{ borderColor: "#e2dccb" }}>
                  {selectedItem.image_url && (
                    <img src={selectedItem.image_url} alt="" className="w-full max-h-64 object-cover rounded-xl mb-6" draggable={false} />
                  )}
                  <div className="font-mono2 text-[10px] tracking-[0.3em] uppercase" style={{ color: "#8a5a1a" }}>SOP · Item</div>
                  <h1 className="font-display text-4xl md:text-5xl mt-2">{selectedItem.name}</h1>
                  <div className="mt-4 flex flex-wrap gap-6 text-sm">
                    <div><span className="opacity-60 mr-2">Selling Price</span>
                      {editingSP ? (
                        <InlineEdit value={editVal} onChange={setEditVal} onSave={() => saveField("sp")} onCancel={() => setEditingSP(false)} testid="sp-edit-input" />
                      ) : (
                        <>
                          <b data-testid="item-sp">₹{selectedItem.sp}</b>
                          {canEdit && (
                            <button data-testid="edit-sp-btn" onClick={() => { setEditVal(String(selectedItem.sp)); setEditingSP(true); }}
                              className="ml-2 opacity-60 hover:opacity-100" title="Edit selling price"><Edit3 size={13} /></button>
                          )}
                          {canSuggest && (
                            <button data-testid="suggest-price-btn" onClick={() => openSuggest("price", "SP")}
                              className="ml-2 opacity-60 hover:opacity-100"><MessageCircleWarning size={14} /></button>
                          )}
                        </>
                      )}
                    </div>
                    <div><span className="opacity-60 mr-2">Unit</span><b>{selectedItem.unit}</b>
                      {canSuggest && (
                        <button data-testid="suggest-unit-btn" onClick={() => openSuggest("unit", "unit")}
                          className="ml-2 opacity-60 hover:opacity-100"><MessageCircleWarning size={14} /></button>
                      )}
                    </div>
                    {canViewMakingCost && selectedItem.making_cost != null && (
                      <div><span className="opacity-60 mr-2">Making Cost</span>
                        {editingCost ? (
                          <InlineEdit value={editVal} onChange={setEditVal} onSave={() => saveField("making_cost")} onCancel={() => setEditingCost(false)} testid="cost-edit-input" />
                        ) : (
                          <>
                            <b data-testid="item-making-cost">₹{Number(selectedItem.making_cost).toFixed(2)}</b>
                            {canEdit && (
                              <button data-testid="edit-cost-btn" onClick={() => { setEditVal(String(selectedItem.making_cost)); setEditingCost(true); }}
                                className="ml-2 opacity-60 hover:opacity-100" title="Edit making cost"><Edit3 size={13} /></button>
                            )}
                          </>
                        )}
                      </div>
                    )}
                    {canViewMakingCost && selectedItem.gross_margin != null && (
                      <div><span className="opacity-60 mr-2">Gross Margin</span><b data-testid="item-gross-margin">₹{Number(selectedItem.gross_margin).toFixed(2)}
                      {selectedItem.gross_margin_pct != null && <span className="opacity-70 ml-1">({Number(selectedItem.gross_margin_pct).toFixed(2)}%)</span>}
                      </b></div>
                    )}
                  </div>

                  <div className="mt-8">
                    <div className="font-mono2 text-[10px] tracking-[0.3em] uppercase mb-3" style={{ color: "#8a5a1a" }}>Ingredients & Costing</div>
                    <ul className="divide-y" style={{ borderColor: "#e6dfcc" }}>
                      {(selectedItem.ingredients || []).map((ing, i) => (
                        <li key={i} className="py-3 flex items-center justify-between group">
                          <div className="flex items-center gap-3">
                            {ing.image_url && <img src={ing.image_url} alt="" className="w-10 h-10 rounded object-cover" draggable={false} />}
                            <div>
                              <div className="font-medium">{ing.name}</div>
                              {ing.unit && <div className="text-xs opacity-60">{ing.unit}</div>}
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="font-mono2 text-sm">₹{ing.cost}</span>
                            {canSuggest && (
                              <button data-testid={`suggest-ing-${i}`} onClick={() => openSuggest("ingredient", ing.name)}
                                className="opacity-40 group-hover:opacity-100 transition-opacity"
                                title="Report discrepancy">
                                <MessageCircleWarning size={16} />
                              </button>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {canViewRecipe && selectedItem.recipe && (
                    <div className="mt-8">
                      <div className="font-mono2 text-[10px] tracking-[0.3em] uppercase mb-2" style={{ color: "#8a5a1a" }}>Recipe</div>
                      <div className="text-sm whitespace-pre-wrap leading-relaxed">{selectedItem.recipe}</div>
                    </div>
                  )}
                  {canViewMethod && selectedItem.method && (
                    <div className="mt-6">
                      <div className="font-mono2 text-[10px] tracking-[0.3em] uppercase mb-2" style={{ color: "#8a5a1a" }}>Method</div>
                      <div className="text-sm whitespace-pre-wrap leading-relaxed">{selectedItem.method}</div>
                    </div>
                  )}
                  {selectedItem.notes && (
                    <div className="mt-8 p-4 rounded-xl" style={{ background: "#f7efd9" }}>
                      <div className="font-mono2 text-[10px] uppercase tracking-widest mb-1" style={{ color: "#8a5a1a" }}>Notes</div>
                      <div className="text-sm whitespace-pre-wrap">{selectedItem.notes}</div>
                    </div>
                  )}
                  {selectedItem.updated_at && (
                    <div className="mt-4 text-[10px] font-mono2 uppercase tracking-widest opacity-50">
                      Last updated {formatIST(selectedItem.updated_at)}
                    </div>
                  )}

                  {canSuggest && (
                    <div className="mt-8 pt-6 border-t flex justify-end" style={{ borderColor: "#e6dfcc" }}>
                      <Button data-testid="suggest-general-btn" onClick={() => openSuggest("general", selectedItem.name)}
                        className="rounded-full bg-[#8a5a1a] hover:bg-[#6b4514] text-white btn-lift">
                        Suggest a change
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </section>
          </div>
          )}
        </main>

        <Dialog open={suggestOpen} onOpenChange={setSuggestOpen}>
          <DialogContent className="bg-[#fffaf0] border-[#e2dccb]">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Suggest a discrepancy</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="text-sm">
                <span className="opacity-60">Field:</span> <b>{sField}</b>
                {sTarget && <> · <span className="opacity-60">Target:</span> <b>{sTarget}</b></>}
              </div>
              <Textarea data-testid="suggest-message" value={sMsg} onChange={e => setSMsg(e.target.value)}
                rows={5} placeholder="e.g., Actual cost of Amul Butter has risen to ₹11 at our vendor..." className="bg-white" />
              <div>
                <div className="text-[10px] font-mono2 uppercase tracking-widest mb-2" style={{ color: "#8a5a1a" }}>Attach a photo (optional)</div>
                <div className="flex items-center gap-3">
                  {sImageUrl ? (
                    <img src={sImageUrl} alt="" className="w-16 h-16 rounded object-cover border" style={{ borderColor: "#e2dccb" }} draggable={false} />
                  ) : (
                    <div className="w-16 h-16 rounded flex items-center justify-center border border-dashed" style={{ borderColor: "#e2dccb", background: "#fffaf0" }}>
                      <MessageCircleWarning size={18} style={{ color: "#B4841F" }} />
                    </div>
                  )}
                  <label className={`inline-flex items-center gap-1 cursor-pointer rounded-full px-3 py-1.5 text-xs font-mono2 uppercase tracking-widest ${sUploading ? "opacity-50 pointer-events-none" : ""}`}
                    style={{ background: "#8a5a1a", color: "white" }}>
                    <input type="file" accept="image/*" onChange={uploadSuggestImage} className="hidden" data-testid="suggest-image-input" />
                    {sUploading ? "Uploading…" : (sImageUrl ? "Replace photo" : "Upload photo")}
                  </label>
                  {sImageUrl && (
                    <button type="button" onClick={() => setSImageUrl("")}
                      className="text-[10px] font-mono2 uppercase tracking-widest" style={{ color: "#c0392b" }}
                      data-testid="suggest-image-remove">Remove</button>
                  )}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="cancel-suggest-btn" variant="ghost" onClick={() => setSuggestOpen(false)}>Cancel</Button>
              <Button data-testid="submit-suggest-btn" onClick={submitSuggest}
                disabled={!sMsg.trim()}
                className="rounded-full bg-[#8a5a1a] hover:bg-[#6b4514] text-white">
                Send to Admin
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AntiCopyGuard>
  );
}

function InlineEdit({ value, onChange, onSave, onCancel, testid }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span>₹</span>
      <Input data-testid={testid} type="number" step="0.01" value={value} onChange={e => onChange(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter") onSave(); if (e.key === "Escape") onCancel(); }}
        className="h-7 w-24 px-2 text-sm bg-white" autoFocus />
      <button data-testid={`${testid}-save`} onClick={onSave} className="text-green-700 hover:text-green-900" title="Save (Enter)"><Check size={14} /></button>
      <button data-testid={`${testid}-cancel`} onClick={onCancel} className="text-red-600 hover:text-red-800" title="Cancel (Esc)"><X size={14} /></button>
    </span>
  );
}
